import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptySessionTemplatesState } from '@/components/features/sessions/templates/EmptySessionTemplatesState';
import { SessionTemplateCardSkeleton } from '@/components/features/sessions/templates/SessionTemplateCardSkeleton';
import { TemplateCard } from '@/components/shared/cards';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { deleteSessionTemplate } from '@/db/session-templates';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useInitialSync } from '@/hooks/useInitialSync';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { useSessionTemplates } from '@/hooks/useSessionTemplates';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { toastError, toastPromise } from '@/lib/toast-helpers';
import { parseSessionTemplateId } from '@/schemas';
import type { SessionTemplate } from '@/schemas';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
export const Route = createFileRoute('/templates/' as any)({
  component: TemplatesPage,
});

function TemplatesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('sessions');
  const { t: tCommon } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const {
    sessionTemplates: templates,
    isLoading: isLoadingTemplates,
    hasData: hasTemplateData,
  } = useSessionTemplates();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTemplate, setDeleteTemplate] = useState<SessionTemplate | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  useDocumentTitle(
    `${t('templates.title', 'Session Templates')} - ${tCommon('app.name', 'One Score')}`,
  );

  const showInitialSkeleton = (isSyncing || isLoadingTemplates) && !hasTemplateData;

  // Search functionality
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return templates.toSorted((a, b) => a.name.localeCompare(b.name));
    }

    const normalizedQuery = searchQuery.toLowerCase().trim();
    return templates
      .filter((template) => {
        const nameMatch = template.name.toLowerCase().includes(normalizedQuery);
        const descriptionMatch = template.description?.toLowerCase().includes(normalizedQuery);
        return nameMatch || descriptionMatch;
      })
      .toSorted((a, b) => a.name.localeCompare(b.name));
  }, [searchQuery, templates]);

  const handleCreateClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
    void navigate({ to: '/templates/new' as any });
  };

  const handleUseClick = (template: SessionTemplate) => {
    const teamConfig = template.teamConfiguration?.teams.map((team) => ({
      id: String(team.id),
      presetId: team.presetId ? String(team.presetId) : undefined,
      name: team.name,
      playerIds: team.playerIds.map(String),
    }));

    void navigate({
      to: '/sessions/new',
      state: {
        gameConfig: template.gameConfig,
        playerIds: template.playerIds.map(String),
        teamConfiguration: teamConfig,
        sessionName: template.defaultSessionName || '',
        sessionNotes: '',
        template,
      } as unknown as Record<string, unknown>,
    });
  };

  const handleEditClick = (template: SessionTemplate) => {
    void navigate({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
      to: '/templates/$templateId/edit' as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment -- Route tree will be regenerated after build
      params: { templateId: String(template.id) } as any,
    });
  };

  const handleDeleteClick = (template: SessionTemplate) => {
    setDeleteTemplate(template);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTemplate) return;

    try {
      setIsDeleting(true);
      const brandedTemplateId = parseSessionTemplateId(String(deleteTemplate.id));

      await toastPromise(deleteSessionTemplate(brandedTemplateId), {
        loading: t('templates.delete.deleting', 'Deleting...'),
        success: t('templates.delete.success', 'Template deleted successfully'),
        error: (error: unknown) =>
          error instanceof Error
            ? error.message
            : t('templates.delete.error', 'Failed to delete template'),
      });

      setDeleteTemplate(undefined);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('templates.delete.error');
      toastError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('templates.title', 'Session Templates')}
          </h1>
          <p className="text-muted-foreground">
            {t(
              'templates.description',
              'Save and reuse session configurations for quick session creation.',
            )}
          </p>
        </div>
        {!showInitialSkeleton && (
          <Button
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
            onClick={() => void navigate({ to: '/templates/new' as any })}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('templates.createButton', 'Create Template')}
          </Button>
        )}
      </div>

      {/* Search */}
      {!showInitialSkeleton && templates.length >= 6 && (
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('templates.search', 'Search templates...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Loading State */}
      {showInitialSkeleton && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SessionTemplateCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!showInitialSkeleton && templates.length === 0 && (
        <EmptySessionTemplatesState onCreateClick={handleCreateClick} />
      )}

      {/* Templates Grid */}
      {!showInitialSkeleton && filteredTemplates.length > 0 && (
        <motion.div
          key={`templates-grid-${routeKey}`}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={slideUp}
          initial={shouldAnimate ? 'initial' : false}
          animate="animate"
          transition={staggerConfig.container}
        >
          <AnimatePresence mode="popLayout">
            {filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                layout
                layoutId={`template-card-${template.id}`}
                variants={slideUp}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <TemplateCard
                  template={template}
                  onUse={handleUseClick}
                  onCreateSession={handleUseClick}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* No Results from Search */}
      {!showInitialSkeleton &&
        templates.length > 0 &&
        filteredTemplates.length === 0 &&
        searchQuery && (
          <div className="flex min-h-[200px] items-center justify-center">
            <p className="text-muted-foreground">{tCommon('sessions.noResults')}</p>
          </div>
        )}

      <DeleteConfirmationDialog
        open={deleteTemplate !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTemplate(undefined);
          }
        }}
        title={t('templates.delete.title', 'Delete Template?')}
        description={t(
          'templates.delete.description',
          'This action cannot be undone. The template will be permanently deleted.',
        )}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        confirmLabel={t('templates.delete.confirm', 'Delete')}
      />
    </div>
  );
}
